import { useCallback, useEffect, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { KeyRound, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";

const ROLE_LABEL = { admin: "Amministratore", user: "Utente" };

const UserRow = ({ u, isSelf, reload }) => {
  const [pwd, setPwd] = useState(null);
  const [confirm, setConfirm] = useState(false);

  const savePwd = async () => {
    if (pwd.length < 8) return toast.error("Minimo 8 caratteri");
    try {
      await api.resetUserPassword(u.id, pwd);
      toast.success(`Password di ${u.name} aggiornata`);
      setPwd(null);
    } catch (e) {
      toast.error(apiError(e));
    }
  };
  const remove = async () => {
    if (!confirm) return setConfirm(true);
    try {
      await api.deleteUser(u.id);
      toast.success("Utente eliminato");
      reload();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div data-testid={`user-row-${u.id}`} className="space-y-2 rounded-xl border bg-slate-50 p-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold">{u.name}{isSelf && <span className="font-normal text-slate-500"> (tu)</span>}</p>
          <p className="truncate text-xs text-slate-500">{u.email}</p>
        </div>
        <span className="rounded-full bg-white px-2 py-0.5 text-[11px] font-medium text-slate-600 ring-1 ring-slate-200">{ROLE_LABEL[u.role]}</span>
        <Button data-testid={`user-reset-${u.id}`} variant="ghost" size="sm" onClick={() => setPwd(pwd === null ? "" : null)} aria-label="Reimposta password">
          <KeyRound className="h-4 w-4" />
        </Button>
        {!isSelf && (
          <Button data-testid={`user-delete-${u.id}`} variant="ghost" size="sm" className="text-rose-600 hover:bg-rose-50" onClick={remove}>
            <Trash2 className="h-4 w-4" /> {confirm && "Conferma"}
          </Button>
        )}
      </div>
      {pwd !== null && (
        <div className="flex gap-2">
          <Input data-testid={`user-new-password-${u.id}`} type="text" placeholder="Nuova password (min. 8)" value={pwd} onChange={(e) => setPwd(e.target.value)} className="h-10 rounded-lg bg-white" />
          <Button data-testid={`user-save-password-${u.id}`} size="sm" className="h-10" onClick={savePwd}>Salva</Button>
        </div>
      )}
    </div>
  );
};

const NewUser = ({ reload }) => {
  const empty = { name: "", email: "", password: "", role: "user" };
  const [form, setForm] = useState(empty);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) return toast.error("La password deve avere almeno 8 caratteri");
    try {
      await api.createUser(form);
      toast.success("Utente creato: comunicagli email e password");
      setForm(empty);
      reload();
    } catch (err) {
      toast.error(apiError(err));
    }
  };
  return (
    <form onSubmit={submit} className="grid grid-cols-2 gap-2 rounded-xl border border-dashed p-3">
      <Input data-testid="new-user-name" required placeholder="Nome" value={form.name} onChange={set("name")} className="h-10 rounded-lg" />
      <Input data-testid="new-user-email" required type="email" placeholder="Email" value={form.email} onChange={set("email")} className="h-10 rounded-lg" />
      <Input data-testid="new-user-password" required type="text" placeholder="Password (min. 8)" value={form.password} onChange={set("password")} className="h-10 rounded-lg" />
      <Select value={form.role} onValueChange={(role) => setForm({ ...form, role })}>
        <SelectTrigger data-testid="new-user-role" className="h-10 rounded-lg"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="user">Utente</SelectItem>
          <SelectItem value="admin">Amministratore</SelectItem>
        </SelectContent>
      </Select>
      <Button data-testid="new-user-add-btn" type="submit" className="col-span-2 h-10 gap-1 rounded-lg"><Plus className="h-4 w-4" /> Aggiungi utente</Button>
    </form>
  );
};

export const UsersDialog = ({ open, onOpenChange, currentUser }) => {
  const [users, setUsers] = useState([]);
  const reload = useCallback(() => {
    api.getUsers().then(setUsers).catch((e) => toast.error(apiError(e)));
  }, []);
  useEffect(() => {
    if (open) {
      reload();
    }
  }, [open, reload]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="users-dialog" className="max-h-[92vh] w-[calc(100%-2rem)] overflow-y-auto rounded-2xl sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display">Utenti autorizzati</DialogTitle>
          <DialogDescription>Solo queste persone possono accedere al planner, con la password oppure con l'account Google della stessa email. Gli amministratori possono anche gestire gli utenti.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          {users.map((u) => (
            <UserRow key={u.id} u={u} isSelf={u.id === currentUser.id} reload={reload} />
          ))}
          <NewUser reload={reload} />
        </div>
      </DialogContent>
    </Dialog>
  );
};
